import * as React from 'react'
import { createPortal } from 'react-dom'
import { type Toast, ToastItem, type ToastVariant } from './toast'

interface ToastContextValue {
  toasts: Toast[]
  addToast: (toast: Omit<Toast, 'id'>) => void
  removeToast: (id: string) => void
  toast: {
    success: (message: string, options?: Partial<Toast>) => void
    error: (message: string, options?: Partial<Toast>) => void
    warning: (message: string, options?: Partial<Toast>) => void
    info: (message: string, options?: Partial<Toast>) => void
    default: (message: string, options?: Partial<Toast>) => void
  }
}

const ToastContext = React.createContext<ToastContextValue | undefined>(undefined)

export interface ToastProviderProps {
  children: React.ReactNode
  maxToasts?: number
  defaultDuration?: number
}

export function ToastProvider({
  children,
  maxToasts = 5,
  defaultDuration = 5000,
}: ToastProviderProps) {
  const [toasts, setToasts] = React.useState<Toast[]>([])

  const addToast = React.useCallback(
    (toast: Omit<Toast, 'id'>) => {
      const id = Math.random().toString(36).substring(2)
      const newToast: Toast = {
        id,
        duration: defaultDuration,
        ...toast,
      }

      setToasts((prev) => {
        const updated = [newToast, ...prev].slice(0, maxToasts)
        return updated
      })
    },
    [maxToasts, defaultDuration],
  )

  const removeToast = React.useCallback((id: string) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id))
  }, [])

  const toastMethods = React.useMemo(
    () => ({
      success: (message: string, options?: Partial<Toast>) =>
        addToast({ message, variant: 'success', ...options }),
      error: (message: string, options?: Partial<Toast>) =>
        addToast({ message, variant: 'error', duration: 7000, ...options }),
      warning: (message: string, options?: Partial<Toast>) =>
        addToast({ message, variant: 'warning', ...options }),
      info: (message: string, options?: Partial<Toast>) =>
        addToast({ message, variant: 'info', ...options }),
      default: (message: string, options?: Partial<Toast>) =>
        addToast({ message, variant: 'default', ...options }),
    }),
    [addToast],
  )

  const value = React.useMemo(
    () => ({
      toasts,
      addToast,
      removeToast,
      toast: toastMethods,
    }),
    [toasts, addToast, removeToast, toastMethods],
  )

  return (
    <ToastContext.Provider value={value}>
      {children}
      <ToastContainer toasts={toasts} onRemove={removeToast} />
    </ToastContext.Provider>
  )
}

interface ToastContainerProps {
  toasts: Toast[]
  onRemove: (id: string) => void
}

function ToastContainer({ toasts, onRemove }: ToastContainerProps) {
  const [mounted, setMounted] = React.useState(false)

  React.useEffect(() => {
    setMounted(true)
  }, [])

  if (!mounted || typeof window === 'undefined') {
    return null
  }

  return createPortal(
    <div
      className="fixed top-4 right-4 z-50 w-full max-w-sm space-y-4 pointer-events-none"
      style={{ maxHeight: 'calc(100vh - 2rem)' }}
    >
      {toasts.map((toast) => (
        <div key={toast.id} className="pointer-events-auto">
          <ToastItem toast={toast} onClose={onRemove} />
        </div>
      ))}
    </div>,
    document.body,
  )
}

export function useToast() {
  const context = React.useContext(ToastContext)
  if (context === undefined) {
    throw new Error('useToast must be used within a ToastProvider')
  }
  return context
}
